import { Button, Checkbox, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";
import { templateConfigsColumnsData } from "shared/constants";
import { ReportItem, useGetReportTemplateHtmlQuery } from "shared/api";
import { renderIsDefaultString } from "shared/helpers";
import { TableButton, TableSearchFilter } from "shared/ui";
import { FilterFilled } from "@ant-design/icons";
import classNames from "classnames";
import { ReportsSorting } from "shared/constants/reports/reports";
import { sortDirections } from "shared/constants/sorting";
import { useReportTypes } from "./useReportTypes";

const {
  name,
  templateId,
  templateHtml,
  reportType,
  reportCategory,
  carboneType,
  isDefault,
  visible,
  actions,
} = templateConfigsColumnsData;

interface UseTableConfigColumnsProps {
  t: TFunction;
  handleEditReport: (record: ReportItem) => void;
  showDeleteReportModal: (record: ReportItem) => void;
  selectedNames: string[];
  nameOptions: {
    label: string;
    value: string;
  }[];
  setSelectedNames: React.Dispatch<React.SetStateAction<string[]>>;
  templateIdValue: string;
  handleTemplateIdChange: (
    e: string | React.ChangeEvent<HTMLInputElement>
  ) => void;
  clearTemplateIdValue: () => void;
  selectedReportTypes: string[];
  setSelectedReportTypes: React.Dispatch<React.SetStateAction<string[]>>;
  reportCategoryValue: string;
  handleReportCategoryChange: (
    e: string | React.ChangeEvent<HTMLInputElement>
  ) => void;
  clearReportCategoryValue: () => void;

  selectedTemplateHtmlValues: string[];
  setSelectedTemplateHtmlValues: React.Dispatch<React.SetStateAction<string[]>>;
  isDefaultValue: boolean | null;
  setIsDefaultValue: React.Dispatch<React.SetStateAction<boolean | null>>;
  changeSort: (ascendOrdering: string, descendOrdering: string) => void;
}

export const useTableConfigColumns = ({
  t,
  handleEditReport,
  showDeleteReportModal,
  nameOptions,
  clearTemplateIdValue,
  handleTemplateIdChange,
  selectedNames,
  setSelectedNames,
  templateIdValue,
  selectedReportTypes,
  setSelectedReportTypes,
  clearReportCategoryValue,
  handleReportCategoryChange,
  isDefaultValue,
  reportCategoryValue,
  setIsDefaultValue,
  selectedTemplateHtmlValues,
  setSelectedTemplateHtmlValues,
  changeSort,
}: UseTableConfigColumnsProps): ColumnsType<ReportItem> => {
  const { reportTypeOptions } = useReportTypes();
  const { data: templateHtmlData } = useGetReportTemplateHtmlQuery();
  const templateHtmlOptions = (templateHtmlData?.result ?? []).map((v) => ({
    label: v,
    value: v,
  }));

  return [
    {
      title: t(name.title),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-600 scale-125": selectedNames.length > 0,
          })}
        />
      ),
      filterDropdown: () => (
        <div style={{ padding: 8, width: 250 }}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            maxTagCount="responsive"
            placeholder={t("Select report names")}
            options={nameOptions}
            value={selectedNames}
            onChange={(vals) => setSelectedNames(vals)}
            style={{ width: "100%" }}
          />
        </div>
      ),
      render: (report: ReportItem) => (
        <Button
          onClick={() => handleEditReport(report)}
          className="border-none hover:border-none bg-transparent shadow-none"
        >
          <span className="underline"> {report?.name}</span>{" "}
        </Button>
      ),
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(ReportsSorting.NAME_ASC, ReportsSorting.NAME_DESC);
        },
      }),
      sorter: true,
    },

    {
      title: t(templateId.title),
      filterIcon: (
        <FilterFilled
          className={
            templateIdValue.length ? "!text-blue-600  scale-125" : undefined
          }
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearTemplateIdValue}
          confirm={confirm}
          value={templateIdValue!}
          handleFilterChange={handleTemplateIdChange!}
          visible={visible}
          placeholder={t("Enter the template id")!}
          title={t("template id")!}
        />
      ),
      render: (report: ReportItem) => report?.template_id ?? "-",
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(
            ReportsSorting.TEMPLATE_ID_ASC,
            ReportsSorting.TEMPLATE_ID_DESC
          );
        },
      }),
      sorter: true,
    },

    {
      title: t(templateHtml.title),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-600 scale-125": selectedTemplateHtmlValues.length > 0,
          })}
        />
      ),
      filterDropdown: () => (
        <div style={{ padding: 8, width: 280 }}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            maxTagCount="responsive"
            placeholder={t("Select template HTML")}
            options={templateHtmlOptions}
            value={selectedTemplateHtmlValues}
            onChange={(vals) => setSelectedTemplateHtmlValues(vals)}
            style={{ width: "100%" }}
          />
        </div>
      ),
      render: (report: ReportItem) => report?.template_html ?? "-",
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(
            ReportsSorting.TEMPLATE_HTML_ASC,
            ReportsSorting.TEMPLATE_HTML_DESC
          );
        },
      }),
      sorter: true,
    },

    {
      title: t(reportType.title),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-600 scale-125": selectedReportTypes.length > 0,
          })}
        />
      ),
      filterDropdown: () => (
        <div style={{ padding: 8, width: 250 }}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder={t("Select report types")}
            options={reportTypeOptions}
            value={selectedReportTypes}
            onChange={(vals) => setSelectedReportTypes(vals)}
            style={{ width: "100%" }}
            maxTagCount="responsive"
          />
        </div>
      ),
      render: (report: ReportItem) => report?.report_type ?? "-",
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(
            ReportsSorting.REPORT_TYPE_ASC,
            ReportsSorting.REPORT_TYPE_DESC
          );
        },
      }),
      sorter: true,
    },

    {
      title: t(reportCategory.title),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-600 scale-125": reportCategoryValue.length > 0,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearReportCategoryValue}
          confirm={confirm}
          value={reportCategoryValue}
          handleFilterChange={handleReportCategoryChange}
          visible={visible}
          placeholder={t("Enter category name") || ""}
          title={t("Filter by Category") || ""}
        />
      ),
      render: (report: ReportItem) => report?.report_category?.name ?? "-",
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(
            ReportsSorting.REPORT_CATEGORY_ASC,
            ReportsSorting.REPORT_CATEGORY_DESC
          );
        },
      }),
      sorter: true,
    },
    {
      title: t(carboneType.title),
      render: (report: ReportItem) => report?.carbone_type ?? "-",
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(
            ReportsSorting.CARBONE_TYPE_ASC,
            ReportsSorting.CARBONE_TYPE_DESC
          );
        },
      }),
      sorter: true,
    },
    {
      title: t(isDefault.title),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-green-600 scale-125": isDefaultValue !== null,
          })}
        />
      ),
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Checkbox
            checked={isDefaultValue === true}
            onChange={(e) => setIsDefaultValue(e.target.checked ? true : null)}
          >
            {t("Show only default")}
          </Checkbox>

          <Checkbox
            checked={isDefaultValue === false}
            onChange={(e) => setIsDefaultValue(e.target.checked ? false : null)}
          >
            {t("Show only non-default")}
          </Checkbox>

          {isDefaultValue !== null && (
            <Button
              type="link"
              size="small"
              onClick={() => setIsDefaultValue(null)}
              style={{ marginTop: 6, padding: 0 }}
            >
              {t("Reset filter")}
            </Button>
          )}
        </div>
      ),
      render: (report: ReportItem) => renderIsDefaultString(report),
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(
            ReportsSorting.IS_DEFAULT_ASC,
            ReportsSorting.IS_DEFAULT_DESC
          );
        },
      }),
      sorter: true,
    },
    {
      title: t(visible.title),
      render: (report: ReportItem) => (report?.visible ? "Visible" : "Hidden"),
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(ReportsSorting.VISIBLE_ASC, ReportsSorting.VISIBLE_DESC);
        },
      }),
      sorter: true,
    },
    {
      title: t(actions.title),
      render: (report: ReportItem) => (
        <TableButton
          type="delete"
          onClick={() => showDeleteReportModal(report)}
        />
      ),
    },
  ];
};
